import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import { showSnackbar } from "@/lib/snackbarUtils";
import {
  DELETE_EDUCATION_PROGRAM_REQUIREMENT_MUTATION,
  pgmProgramTableRefetchQueries,
  type DeleteEducationProgramRequirementMutationResponse,
  type DeleteEducationProgramRequirementMutationVariables
} from "./mutations";

export type ProgramRequirementToDelete = {
  description: string;
  requireId: string;
  levelCode: string;
};

export default function DeleteProgramRequirementDialog({
  open,
  requirement,
  pgaId,
  onClose
}: {
  open: boolean;
  requirement: ProgramRequirementToDelete | null;
  pgaId: string;
  onClose: () => void;
}) {
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setDeleteError(null);
  }, [open]);

  const [deleteRequirement, { loading }] = useMutation<
    DeleteEducationProgramRequirementMutationResponse,
    DeleteEducationProgramRequirementMutationVariables
  >(DELETE_EDUCATION_PROGRAM_REQUIREMENT_MUTATION, {
    refetchQueries: pgmProgramTableRefetchQueries(pgaId),
    awaitRefetchQueries: true
  });

  const handleConfirm = async () => {
    if (!requirement) return;

    setDeleteError(null);

    try {
      const { data } = await deleteRequirement({
        variables: {
          input: {
            pgaId,
            requireId: requirement.requireId,
            levelCode: requirement.levelCode
          }
        }
      });

      if (data?.deleteEducationProgramRequirement.success) {
        showSnackbar("Requirement deleted");
        onClose();
      } else {
        setDeleteError(data?.deleteEducationProgramRequirement.message ?? "Unable to delete requirement");
      }
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : "Unable to delete requirement");
    }
  };

  return (
    <Dialog
      open={open}
      headline="Delete Requirement?"
      onClosed={onClose}
      actions={
        <>
          <Button variant="text" onClick={onClose} disabled={loading} testId="delete-requirement-cancel">
            Cancel
          </Button>
          <Button
            variant="filled"
            theme="error"
            onClick={handleConfirm}
            disabled={loading || !requirement}
            testId="delete-requirement-confirm"
          >
            Delete
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {deleteError && (
          <Banner variant="error" description={deleteError} inline closable={false} hideSecondaryDismiss />
        )}
        <p className="text-body-medium">
          This will remove {requirement?.description ? <strong>{requirement.description}</strong> : "this requirement"}{" "}
          from this person's PGM program history. This cannot be undone.
        </p>
      </div>
    </Dialog>
  );
}
